import { NULL_VALUE } from 'constants/displayConstants';

export const LEGACY_NOT_ARRIVED = 'Not Arrived';
export const LEGACY_CHECKED_OUT = 'Checked-Out';
export const NOT_YET_IN_TRANSIT = 'Not Yet In Transit';
export const CHECKED_OUT = 'Checked Out';
export const IN_ORGANIZATION = 'In Organization';

export function normalizeCheckInStatus(
  status: string | null | undefined
): string {
  if (!status || status === IN_ORGANIZATION) return '';
  if (status === LEGACY_NOT_ARRIVED) return NOT_YET_IN_TRANSIT;
  if (status === LEGACY_CHECKED_OUT) return CHECKED_OUT;
  return status;
}

export const CHECK_IN_STATUS_COLUMNS = [
  NOT_YET_IN_TRANSIT,
  'In Transit',
  'On Site',
  'Checked In',
  CHECKED_OUT,
  'Demobilizing',
  'Demobilized',
] as const;

export type CheckInStatusColumn = (typeof CHECK_IN_STATUS_COLUMNS)[number];

export const CHECK_IN_STATUS_OPTIONS = [
  { value: NOT_YET_IN_TRANSIT, label: NOT_YET_IN_TRANSIT },
  { value: 'In Transit', label: 'In Transit' },
  { value: 'On Site', label: 'On Site' },
  { value: 'Checked In', label: 'Checked In' },
  { value: CHECKED_OUT, label: CHECKED_OUT },
  { value: 'Demobilizing', label: 'Demobilizing' },
];

const EDITABLE_CHECK_IN_STATUSES = new Set([
  LEGACY_NOT_ARRIVED,
  LEGACY_CHECKED_OUT,
  IN_ORGANIZATION,
  '',
  ...CHECK_IN_STATUS_OPTIONS.map((option) => option.value),
]);

export function isAssignedToIncident(resource: {
  incident?: { id?: string } | null;
  incident_details?: { id?: string }[] | null;
  incidents?: string[] | null;
}): boolean {
  if (resource.incident?.id) return true;
  if (resource.incident_details?.some((incident) => incident?.id)) return true;
  if (resource.incidents?.some(Boolean)) return true;
  return false;
}

export function effectiveCheckInStatus(
  status: string | null | undefined,
  assignedToIncident: boolean
): string {
  if (!assignedToIncident) return '';
  return normalizeCheckInStatus(status);
}

export function isCheckInStatusEditable(
  status: string | null | undefined
): boolean {
  return EDITABLE_CHECK_IN_STATUSES.has(status ?? '');
}

export function matchesCheckInStatusFilter(
  status: string | null | undefined,
  selected: string[],
  assignedToIncident: boolean
): boolean {
  if (selected.length === 0) return true;
  const effective = effectiveCheckInStatus(status, assignedToIncident);
  return selected.some((value) => normalizeCheckInStatus(value) === effective);
}

export function expandCheckInStatusFilters(selected: string[]): string[] {
  const next = [...selected];
  if (
    selected.includes(NOT_YET_IN_TRANSIT) &&
    !selected.includes(LEGACY_NOT_ARRIVED)
  ) {
    next.push(LEGACY_NOT_ARRIVED);
  }
  if (selected.includes(CHECKED_OUT) && !selected.includes(LEGACY_CHECKED_OUT)) {
    next.push(LEGACY_CHECKED_OUT);
  }
  return next;
}

export function formatCheckInStatus(
  status: string | null | undefined,
  assignedToIncident: boolean
): string {
  return effectiveCheckInStatus(status, assignedToIncident) || NULL_VALUE;
}

export function persistCheckInStatus(
  status: string | null | undefined,
  assignedToIncident: boolean
): string {
  return effectiveCheckInStatus(status, assignedToIncident);
}
